'use client'

import { CSSProperties, Fragment, useEffect, useRef } from 'react'
import { Transition } from '@headlessui/react'
import { shallowEqual, useActorRef, useSelector } from '@xstate/react'
import { twJoin } from 'tailwind-merge'
import { assign, createMachine, fromCallback } from 'xstate'
import { z } from 'zod'

import { machineTypes } from '@/utils/machineTypes'
import { Logo } from '../Logo'
import { AnimatedHeadingStrings } from './StackedHeading'

interface AnimatedStackedHeadingProps {
  classNames?: [string?, string?]
  lines: AnimatedHeadingStrings
  small?: boolean
  sameHeight?: boolean
  delay?: number
  duration?: number
}

const loopMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QBkD2qAOA6AkhANmAMQDaADALqKgaqwCWALvagHbUgAeiAzABxYArAHZBfAIzjBIsuOHyANCACeiAGyysZAEzi+AFjIBONX21HhPAL5WlaTFgDqqAE4BreqyhEAavQYARvT4TMoABADGABYAhl6Q5FRIILQMzGwc3AgAtJJqWDzC+uKmwuLaZMJq2kqqCHyWWBp64jz8PIIV2jZ26NjO7p5QWH6BhEScsIwxjGBYMQBmsy4AFAASAKIAggAiGwBKAPo7AKr7WwAqOADyAHIAlET2-a4eXiP+9AGEiRypTCx2Mksp0mtpOnwyGZhLI+HxaohJPoBGROoIdEYeGpTOCeiBnk5XkNfJ8giFGOFonEYBASOIkjQ6ACMsDEeJ9Fg+O19BpRNiyPphAiEEZkVgSuIyDwjF0yNi1DZbCBWKgIHAOM8-kz0kDQFlsrptAUiiUGuVKtVhfosQUeAKeIK9OZLHiCXhCFq0oDMogDTxOWoSmoYXbDPpBGphZ1-Vz7Y6zBZrEqCQM3lBPczdVxfSUOYViqVzVUaip1HCmrJhBY+EYzA0jK6+oTBu9Rl8Pcl-jqfQh-doOuIZfK+Gpa8J4aWEJKOlo2lUTKJpEneg5U0MsGt6BA1Vmu97WQhDUJJXoazzUZ0S3Vp4JZ4VR8HpIIkzYgA */
    id: 'Loop',
    types: machineTypes<{
      context: { counter: number; strings: [string, string][] }
      input: Partial<{ strings: [string, string][] }>
      events: { type: 'Set strings'; strings: [string, string][] } | { type: 'Visibility changed' }
    }>(),
    context: ({ input }) => ({
      counter: 0,
      strings: input.strings ?? [],
    }),
    initial: 'Idle',
    states: {
      Idle: {
        always: {
          target: 'Working',
          guard: 'Headers strings are valid',
        },
      },

      Working: {
        states: {
          Visible: {
            after: {
              HEADER_DURATION: {
                target: 'Visible',
                reenter: true,
                actions: 'Increment counter',
              },
            },
          },
          Hidden: {},
        },

        initial: 'Visible',

        invoke: {
          src: 'Watch window visibility',
          id: 'watchWindowVisibility',
        },

        description: `Use the Window VisibilityState API to detech if the window is in focus.`,

        on: {
          'Visibility changed': [
            {
              target: '.Visible',
              guard: 'Window is visible',
            },
            '.Hidden',
          ],
        },
      },
    },
  },
  {
    delays: {
      HEADER_DURATION: 8000,
    },
    actions: {
      'Increment counter': assign({
        counter: ({ context }) => (context.strings.length === 0 ? 0 : (context.counter + 1) % context.strings.length),
      }),
    },
    guards: {
      'Headers strings are valid': ({ context }) =>
        z
          .array(z.tuple([z.string(), z.string()]))
          .min(2)
          .safeParse(context.strings).success,
      'Window is visible': () => document.visibilityState === 'visible',
    },
    actors: {
      'Watch window visibility': fromCallback(({ sendBack }) => {
        const handleVisibilityChange = () => {
          sendBack({ type: 'Visibility changed' })
        }

        window.addEventListener('visibilitychange', handleVisibilityChange)

        handleVisibilityChange()

        return () => window.removeEventListener('visibilitychange', handleVisibilityChange)
      }),
    },
  },
)

export function AnimatedStackedHeading({
  classNames: [firstLineClass, subsequentLineClass] = [undefined, undefined],
  lines,
  small,
  sameHeight,
  delay = 8000,
  duration,
}: AnimatedStackedHeadingProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const parentRef = useRef<HTMLHeadingElement>(null)
  const actor = useActorRef(loopMachine.provide({ delays: { HEADER_DURATION: delay } }), { input: { strings: lines } })
  const { label, counter, strings } = useSelector(
    actor,
    ({ context: { counter, strings } }) => {
      const [firstLine = '', subsequentLine = ''] = strings[counter]
      const label = `${firstLine} ${subsequentLine}`
      return { label, counter, strings }
    },
    shallowEqual,
  )

  const uniqueFirstLines = Array.from(new Set(lines.map(([firstLine]) => firstLine)))
  const uniqueSubsequentLines = Array.from(new Set(lines.map(([, subsequentLine]) => subsequentLine)))

  useEffect(() => {
    const parentElement = ref.current?.closest('h2')
    if (parentElement && !parentRef.current) {
      parentRef.current = parentElement
    }

    parentRef.current?.setAttribute('aria-label', label)
  }, [label])

  const commonTransitionAttributes = {
    style: {
      '--transition-duration': `${duration || delay / 2}ms`,
      '--transition-delay': `${delay / 8}ms`,
    } as CSSProperties,
    enterFrom: 'opacity-0 blur-lg',
    enter: 'transition-[filter,opacity] duration-[--transition-duration] ease-in-out delay-[--transition-delay]',
    enterTo: 'opacity-100 blur-none',
    leaveFrom: 'opacity-100 blur-none',
    leaveTo: 'opacity-0 blur-lg',
    leave: 'transition-[filter,opacity] duration-[--transition-duration] ease-in-out',
    as: 'span',
  } as const

  return (
    <Fragment>
      {uniqueFirstLines.map((firstLine) => (
        <Transition
          key={firstLine}
          show={firstLine === strings[counter][0]}
          className={firstLineClass}
          {...commonTransitionAttributes}
        >
          <span ref={ref}>{firstLine}</span>
        </Transition>
      ))}
      {uniqueSubsequentLines.map((subsequentLine) => (
        <Transition
          key={subsequentLine}
          show={subsequentLine === strings[counter][1]}
          className={subsequentLineClass}
          {...commonTransitionAttributes}
        >
          {subsequentLine === 'DUET' ? (
            <Logo
              className={twJoin('h-12 w-52', !small && 'sm:h-16 sm:w-72', !sameHeight && !small && 'md:h-20 md:w-96')}
            />
          ) : (
            subsequentLine
          )}
        </Transition>
      ))}
    </Fragment>
  )
}
